import type { Theme, Tool } from '@blackboard/shared';

export interface ThemeSpec {
  id: Theme;
  name: string;
  tool: Tool;

  // Canvas surface
  bgColor: string;
  bgPattern: 'wood-frame' | 'none';
  chalkTexture: boolean;

  // Brushes
  palette: string[];
  sizes: [number, number, number];

  // Typography vibe
  fontFamily: string;

  // UI surfaces (panels, toolbar, switcher, presence)
  surface: string;          // panel fill
  surfaceBorder: string;    // 1px border on panels
  surfaceShadow: string;    // shadow for the panel
  surfaceText: string;      // primary panel text
  surfaceTextMuted: string; // secondary text on panels

  // Interactive states on panels
  hoverBg: string;
  pressedBg: string;
  selectedBg: string;
  selectedRing: string;

  // Accent used for highlights and brand
  accent: string;
  accentContrast: string;   // text color that reads on accent

  // Divider line inside panels
  divider: string;

  // Deprecated (kept for any old reference). New code should use `surface`/`surfaceText`.
  toolbarBg: string;
  toolbarFg: string;
}

// Classic green chalkboard — warm, nostalgic, hand-drawn classroom
const classic: ThemeSpec = {
  id: 'classic_blackboard',
  name: '经典黑板',
  tool: 'chalk',
  bgColor: '#1b4a2c',
  bgPattern: 'wood-frame',
  chalkTexture: true,
  palette: ['#ffffff', '#fde68a', '#fbbf24', '#f87171', '#f472b6', '#a78bfa', '#60a5fa', '#34d399'],
  sizes: [3, 6, 10],
  fontFamily: '"Kalam",ui-rounded,system-ui,sans-serif',

  surface: 'rgba(22,58,36,0.78)',
  surfaceBorder: 'rgba(255,255,255,0.10)',
  surfaceShadow: '0 12px 40px -12px rgba(0,0,0,0.55), 0 2px 0 rgba(255,255,255,0.06) inset',
  surfaceText: '#f4f4f5',
  surfaceTextMuted: 'rgba(244,244,245,0.92)',

  hoverBg: 'rgba(255,255,255,0.08)',
  pressedBg: 'rgba(255,255,255,0.14)',
  // 紫金 selected states — the violet reads on the classic green surface
  selectedBg: 'rgba(109,76,140,0.28)', // brand-500 @ 28%
  selectedRing: '#a98ab9', // brand-300 — high-luma violet

  accent: '#fde68a',
  accentContrast: '#1b4a2c',

  divider: 'rgba(255,255,255,0.14)',

  toolbarBg: 'rgba(22,58,36,0.78)',
  toolbarFg: '#f4f4f5',
};

// Pure dark OLED — minimal, high-contrast, neon chalk
const dark: ThemeSpec = {
  id: 'dark_blackboard',
  name: '纯黑板',
  tool: 'chalk',
  bgColor: '#0a0a0a',
  bgPattern: 'none',
  chalkTexture: true,
  palette: ['#ffffff', '#fde047', '#fb923c', '#f43f5e', '#e879f9', '#a3e635', '#06b6d4', '#22d3ee'],
  sizes: [3, 6, 10],
  fontFamily: 'ui-sans-serif,system-ui,-apple-system,"Segoe UI",sans-serif',

  surface: 'rgba(18,18,20,0.78)',
  surfaceBorder: 'rgba(255,255,255,0.08)',
  surfaceShadow: '0 20px 60px -20px rgba(0,0,0,0.9), 0 1px 0 rgba(255,255,255,0.06) inset',
  surfaceText: '#f4f4f5',
  surfaceTextMuted: 'rgba(244,244,245,0.90)',

  hoverBg: 'rgba(255,255,255,0.06)',
  pressedBg: 'rgba(255,255,255,0.12)',
  selectedBg: 'rgba(34,211,238,0.18)',
  selectedRing: '#22d3ee',

  accent: '#22d3ee',
  accentContrast: '#0a0a0a',

  divider: 'rgba(255,255,255,0.10)',

  toolbarBg: 'rgba(18,18,20,0.78)',
  toolbarFg: '#f4f4f5',
};

// Whiteboard — clean, productive, Figma/Miro feel
const white: ThemeSpec = {
  id: 'whiteboard',
  name: '白板',
  tool: 'marker',
  bgColor: '#fafafa',
  bgPattern: 'none',
  chalkTexture: false,
  palette: ['#111827', '#dc2626', '#ea580c', '#f59e0b', '#16a34a', '#0891b2', '#2563eb', '#9333ea'],
  sizes: [2, 4, 8],
  fontFamily: 'ui-sans-serif,system-ui,-apple-system,"Segoe UI",sans-serif',

  surface: 'rgba(255,255,255,0.88)',
  surfaceBorder: 'rgba(17,24,39,0.08)',
  surfaceShadow: '0 14px 40px -12px rgba(17,24,39,0.18), 0 1px 0 rgba(255,255,255,0.8) inset',
  surfaceText: '#111827',
  surfaceTextMuted: 'rgba(17,24,39,0.84)',

  hoverBg: 'rgba(17,24,39,0.05)',
  pressedBg: 'rgba(17,24,39,0.10)',
  selectedBg: 'rgba(37,99,235,0.12)',
  selectedRing: '#2563eb',

  accent: '#2563eb',
  accentContrast: '#ffffff',

  divider: 'rgba(17,24,39,0.10)',

  toolbarBg: 'rgba(255,255,255,0.88)',
  toolbarFg: '#111827',
};

export const THEMES: Record<Theme, ThemeSpec> = {
  classic_blackboard: classic,
  dark_blackboard: dark,
  whiteboard: white,
};

export function getTheme(id: Theme): ThemeSpec {
  return THEMES[id];
}
